import { useId, useState } from "react";

export type Bar = { label: string; value: number; secondary?: number; title?: string };

/**
 * Single-series bar chart in the brand primary (thin marks, 4px rounded data-ends anchored to the
 * baseline, recessive grid, hover tooltip). Optional `secondary` renders a muted overlay bar (e.g. wins
 * within games) — same hue, lighter step, so it stays one series visually.
 */
export function BarChart({
  bars,
  height = 160,
  valueFormat = (v: number) => String(v),
  ariaLabel,
}: {
  bars: Bar[];
  height?: number;
  valueFormat?: (v: number) => string;
  ariaLabel: string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | undefined>(undefined);
  const width = Math.max(320, bars.length * 36);
  const pad = { top: 16, right: 8, bottom: 28, left: 32 };
  const max = Math.max(1, ...bars.map((b) => b.value));
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const slot = innerW / Math.max(1, bars.length);
  const barW = Math.min(20, slot * 0.6);
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const ticks = [0, max / 2, max];
  const hovered = hover === undefined ? undefined : bars[hover];

  return (
    <div className="pr-chart" style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby={`${id}-title`}>
        <title id={`${id}-title`}>{ariaLabel}</title>
        {ticks.map((t) => (
          <g key={t}>
            <line
              className="pr-chart__grid"
              x1={pad.left}
              x2={width - pad.right}
              y1={y(t)}
              y2={y(t)}
            />
            <text className="pr-chart__label" x={pad.left - 6} y={y(t) + 4} textAnchor="end">
              {valueFormat(Math.round(t))}
            </text>
          </g>
        ))}
        {bars.map((bar, i) => {
          const x = pad.left + i * slot + (slot - barW) / 2;
          const h = Math.max(0, y(0) - y(bar.value));
          const r = Math.min(4, h / 2, barW / 2);
          const path =
            h === 0
              ? ""
              : `M${x},${y(0)} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${barW - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} z`;
          const sh = bar.secondary === undefined ? 0 : Math.max(0, y(0) - y(bar.secondary));
          const sr = Math.min(4, sh / 2, barW / 2);
          const spath =
            sh === 0
              ? ""
              : `M${x},${y(0)} v${-(sh - sr)} a${sr},${sr} 0 0 1 ${sr},${-sr} h${barW - 2 * sr} a${sr},${sr} 0 0 1 ${sr},${sr} v${sh - sr} z`;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: hover-only tooltip; values are in the accessible title
            <g
              key={bar.label}
              className="pr-chart__group"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(undefined)}
            >
              <rect
                className="pr-chart__hit"
                x={pad.left + i * slot}
                y={pad.top}
                width={slot}
                height={innerH}
              />
              {path && (
                <path
                  className={bar.secondary === undefined ? "pr-chart__bar" : "pr-chart__bar--muted"}
                  d={path}
                />
              )}
              {spath && <path className="pr-chart__bar" d={spath} />}
              {(bars.length <= 12 || i % Math.ceil(bars.length / 12) === 0) && (
                <text
                  className="pr-chart__label"
                  x={x + barW / 2}
                  y={height - 8}
                  textAnchor="middle"
                >
                  {bar.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hovered && hover !== undefined && (
        <div
          className="pr-tooltip"
          style={{
            left: `${((pad.left + hover * slot + slot / 2) / width) * 100}%`,
            top: 0,
            transform: "translate(-50%, -100%)",
          }}
        >
          <strong>{hovered.title ?? hovered.label}</strong>
          <div>
            {valueFormat(hovered.value)}
            {hovered.secondary !== undefined ? ` · ${valueFormat(hovered.secondary)} wins` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
